import type { CSSProperties } from "react";

interface PercentileRankMiniBarProps {
  percentile: number | null | undefined;
  pendingLabel?: string;
  width?: number;
  showPrefix?: boolean;
}

const AXIS_MIN = 1;
const AXIS_MAX = 99;
const AXIS_RANGE = AXIS_MAX - AXIS_MIN;

function percentileToTrackPct(percentile: number): number {
  return ((Math.max(AXIS_MIN, Math.min(AXIS_MAX, percentile)) - AXIS_MIN) / AXIS_RANGE) * 100;
}

type PercentileBand = "very-low" | "low" | "on-track";

function percentileBand(percentile: number): PercentileBand {
  if (percentile <= 15) return "very-low";
  if (percentile <= 35) return "low";
  return "on-track";
}

function bandTone(percentile: number): { fg: string; stroke: string } {
  const band = percentileBand(percentile);
  if (band === "very-low") return { fg: "var(--risk-concern-fg)", stroke: "var(--risk-concern)" };
  if (band === "low") return { fg: "var(--risk-emerging-fg)", stroke: "var(--risk-emerging)" };
  return { fg: "var(--risk-on-track-fg)", stroke: "var(--risk-on-track)" };
}

function pendingStyle(): CSSProperties {
  return {
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    fontWeight: 700,
  };
}

export function PercentileRankMiniBar({
  percentile,
  pendingLabel = "Pending",
  width = 104,
  showPrefix = false,
}: PercentileRankMiniBarProps) {
  if (percentile == null || !Number.isFinite(percentile)) {
    return <span style={pendingStyle()}>{pendingLabel}</span>;
  }

  const display = Math.max(1, Math.min(99, Math.round(percentile)));
  const markerPct = percentileToTrackPct(display);
  const veryLowPct = percentileToTrackPct(15);
  const lowPct = percentileToTrackPct(35) - veryLowPct;
  const onTrackPct = 100 - veryLowPct - lowPct;
  const activeBand = percentileBand(display);
  const inactiveOpacity = 0.32;
  const bandOpacity = (band: PercentileBand) => (band === activeBand ? 1 : inactiveOpacity);
  const tone = bandTone(display);
  const label = showPrefix ? `PR${display}` : String(display);

  return (
    <div
      aria-label={`Percentile rank ${display}`}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "flex-end",
        justifyContent: "center",
        width: width + 20,
        height: 28,
        paddingTop: 14,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: "relative",
          display: "inline-flex",
          width,
          height: 8,
          borderRadius: 999,
          overflow: "visible",
          border: "1px solid var(--border-subtle)",
          background: "var(--ink-100)",
          boxSizing: "border-box",
        }}
      >
        <span style={{ width: `${veryLowPct}%`, height: "100%", borderRadius: "999px 0 0 999px", background: "var(--risk-concern)", opacity: bandOpacity("very-low") }} />
        <span style={{ width: `${lowPct}%`, height: "100%", background: "var(--risk-emerging)", opacity: bandOpacity("low") }} />
        <span style={{ width: `${onTrackPct}%`, height: "100%", borderRadius: "0 999px 999px 0", background: "var(--risk-on-track)", opacity: bandOpacity("on-track") }} />
        <span
          style={{
            position: "absolute",
            left: `${markerPct}%`,
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: "var(--ink-0)",
            border: `2px solid ${tone.stroke}`,
            boxShadow: "0 1px 3px color-mix(in oklab, var(--ink-900) 22%, transparent)",
          }}
        />
        <span
          style={{
            position: "absolute",
            left: `${markerPct}%`,
            bottom: 12,
            transform: "translateX(-50%)",
            minWidth: showPrefix ? 30 : 22,
            textAlign: "center",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            lineHeight: 1,
            fontWeight: 900,
            color: tone.fg,
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
      </span>
    </div>
  );
}
